import { useEffect } from 'react'
import { useI18n } from './LanguageContext'
import { languages, pathFor } from './languages'

const SITE = 'https://nanovitsa.com'
const localeByCode = Object.fromEntries(languages.map((lang) => [lang.code, lang.locale]))

function upsertMeta(selector, attrs) {
  let el = document.querySelector(selector)
  if (!el) {
    el = document.createElement('meta')
    Object.entries(attrs).forEach(([key, value]) => {
      if (key !== 'content') el.setAttribute(key, value)
    })
    document.head.appendChild(el)
  }
  el.setAttribute('content', attrs.content)
}

function upsertLink(rel, href, extra = {}) {
  const selectorParts = [`link[rel="${rel}"]`, ...Object.entries(extra).map(([k, v]) => `[${k}="${v}"]`)]
  let el = document.querySelector(selectorParts.join(''))
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    Object.entries(extra).forEach(([key, value]) => el.setAttribute(key, value))
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id)
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.id = id
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data, null, 2)
}

// Keeps <head> in sync with the active language on this client-rendered page:
// title, description/keywords, Open Graph & Twitter tags, canonical + hreflang
// alternates for all four languages, and the two JSON-LD blocks.
function Seo() {
  const { lang, t } = useI18n()

  useEffect(() => {
    const url = SITE + pathFor(lang)

    document.title = t.meta.title
    upsertMeta('meta[name="description"]', { name: 'description', content: t.meta.description })
    upsertMeta('meta[name="keywords"]', { name: 'keywords', content: t.meta.keywords })
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: t.meta.ogTitle })
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: t.meta.ogDescription })
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url })
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: localeByCode[lang] })
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: t.meta.twitterTitle })
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: t.meta.twitterDescription })

    upsertLink('canonical', url)
    languages.forEach((entry) => upsertLink('alternate', SITE + entry.path, { hreflang: entry.code }))
    upsertLink('alternate', SITE + pathFor('de'), { hreflang: 'x-default' })

    upsertJsonLd('schema-place', {
      '@context': 'https://schema.org',
      '@type': 'Place',
      name: 'Nanovitsa',
      alternateName: 'Нановица',
      description: t.meta.schemaPlaceDescription,
      url,
      inLanguage: lang,
      geo: { '@type': 'GeoCoordinates', latitude: 41.5511, longitude: 25.5701, elevation: 308 },
      address: {
        '@type': 'PostalAddress',
        postalCode: '6830',
        addressLocality: 'Nanovitsa',
        addressRegion: 'Kardzhali Province',
        addressCountry: 'BG',
      },
      containedInPlace: { '@type': 'AdministrativeArea', name: 'Momchilgrad Municipality, Kardzhali Province, Bulgaria' },
    })
    upsertJsonLd('schema-tatul', {
      '@context': 'https://schema.org',
      '@type': 'TouristAttraction',
      name: t.meta.schemaTatulName,
      alternateName: 'Татул',
      description: t.meta.schemaTatulDescription,
      url: `${url}#explore`,
      inLanguage: lang,
      geo: { '@type': 'GeoCoordinates', latitude: 41.5536, longitude: 25.5411 },
      containedInPlace: { '@type': 'AdministrativeArea', name: 'Momchilgrad Municipality, Kardzhali Province, Bulgaria' },
    })
  }, [lang, t])

  return null
}

export default Seo
